import { Injectable } from '@angular/core';
import { Record } from '../../offline-first';
import { cloneDeep } from 'lodash';
import { v4 as uuid } from 'uuid';
import { Exercise, Resource, ResourceType } from '../models/exercise-program-document';
import { Phase, Plan, PlannedTier, PlannedTierRow, Schedule, Workout } from '../models/plan-document';
import { UserLibrary, Template } from '../models/user-library-document';
import { Observation } from '../models/observation-document';
import moment from 'moment';

export enum DocumentModelType {
  Phase,
  Plan,
  PlannedTier,
  PlannedTierRow,
  Schedule,
  Template,
  UserLibrary,
  Workout,
  Observation,
  Exercise,
  VideoResource,
}

@Injectable({
  providedIn: 'root',
})
export class DocumentModelFactoryService {
  private builders: Map<DocumentModelType, () => any>;
  private inititalized = false;

  constructor() {
    this.init();
  }

  private init() {
    if (!this.inititalized) {
      this.builders = new Map<DocumentModelType, () => any>();
      this.builders.set(DocumentModelType.Phase, PhaseBuilder);
      this.builders.set(DocumentModelType.Plan, PlanBuilder);
      this.builders.set(DocumentModelType.PlannedTier, PlannedTierBuilder);
      this.builders.set(DocumentModelType.PlannedTierRow, PlannedTierRowBuilder);
      this.builders.set(DocumentModelType.Schedule, ScheduleBuilder);
      this.builders.set(DocumentModelType.Template, TemplateBuilder);
      this.builders.set(DocumentModelType.UserLibrary, UserLibraryDocumentBuilder);
      this.builders.set(DocumentModelType.Workout, WorkoutBuilder);
      this.builders.set(DocumentModelType.Observation, ObservationBuilder);
      this.builders.set(DocumentModelType.Exercise, ExerciseBuilder);
      this.builders.set(DocumentModelType.VideoResource, VideoResourceBuilder);
    }
  }

  build<T>(type: DocumentModelType): T {
    const builder = this.builders.get(type);
    return builder ? builder() : (null as T);
  }

  copy<T extends Record>(source: T) {
    const copy = cloneDeep<T>(source);
    copy.id = uuid();

    return copy as T;
  }
}

function PhaseBuilder(): Phase {
  return {
    id: uuid(),
    end_day: null,
    end_week: null,
    name: null,
    start_day: null,
    start_week: null,
  } as Phase;
}

function PlanBuilder(): Plan {
  return {
    id: uuid(),
    exercise_program_id: null,
    exercise_program_version: null,
    exercises: [],
    name: null,
    num_weeks: null,
    phases: [],
    start_date: null,
    type: 'Plan',
    version: null,
    workouts: [],
  } as Plan;
}

function PlannedTierBuilder(): PlannedTier {
  return {
    id: uuid(),
    name: 'Custom Tier 1',
    rows: [],
    order: 1,
    notes: '',
  } as PlannedTier;
}

function PlannedTierRowBuilder(): PlannedTierRow {
  return {
    id: uuid(),
    exercise_id: null,
    exercise_name: null,
    notes: '',
    parameter_selections: {},
  } as PlannedTierRow;
}

function ScheduleBuilder(): Schedule {
  return {
    day: [],
    week: [],
  } as Schedule;
}

function TemplateBuilder(): Template {
  return {
    id: uuid(),
    name: null,
    template: null,
  };
}

function UserLibraryDocumentBuilder(): UserLibrary {
  return {
    id: uuid(),
    exercise_program_id: null,
    exercise_program_version: null,
    name: 'User Library',
    rows: [],
    tiers: [],
    type: 'UserLibrary',
    user: null,
    version: null,
    workouts: [],
  };
}

function WorkoutBuilder(): Workout {
  return {
    id: uuid(),
    name: 'Custom Day',
    schedule: null,
    tiers: [],
    version: 0,
    est_completion_time_ms: -1,
    est_rpe: -1,
  } as Workout;
}

function ObservationBuilder(): Observation {
  return {
    id: uuid(),
    measure: null,
    result: null,
    timestamp: moment().toISOString(),
  };
}

function ExerciseBuilder(): Exercise {
  return {
    id: uuid(),
    name: null,
    parameters: {},
    resources: [],
    exercise_categories: [],
  };
}

function VideoResourceBuilder(): Resource {
  return {
    id: uuid(),
    hls_url: null,
    name: '',
    title: '',
    type: ResourceType.Video,
  };
}
